import {
  Conversation,
  ConversationParticipant,
} from '@nxsys/store/slices/message/types';

type CurrentUser = {
  id: string;
  subcontractorId?: {
    companyPayment?: {
      companyName?: string;
    };
  };
  companyPayment?: {
    companyName?: string;
  };
};

type DisplayResult = {
  label: 'From' | 'To';
  name: string;
  names: string[];
};

const formatName = (forename?: string, surname?: string): string =>
  `${forename || ''} ${surname || ''}`.trim() || 'Unknown';

const getUmbrellaCompanyName = (currentUser: CurrentUser): string | undefined =>
  currentUser?.subcontractorId?.companyPayment?.companyName ||
  currentUser?.companyPayment?.companyName;

/**
 * Get sender display name based on isBureau and creator's role
 */
export const getConversationSenderName = (
  conv: Conversation,
  currentUser: CurrentUser,
  isBureau: boolean,
): string => {
  const createdBy = conv.createdBy;
  if (!createdBy) return 'Unknown';

  if (isBureau) {
    return formatName(createdBy.forename, createdBy.surname);
  }

  // Umbrella
  if (createdBy.roles === 'admin') {
    return getUmbrellaCompanyName(currentUser) || 'Admin';
  }

  if (createdBy.roles === 'agency') {
    return (
      createdBy.agencyName || formatName(createdBy.forename, createdBy.surname)
    );
  }

  return formatName(createdBy.forename, createdBy.surname);
};

/**
 * Get recipient display names from participants as an array
 */
export const getConversationRecipientNameList = (
  conv: Conversation,
  currentUser: CurrentUser,
): string[] => {
  const participants = conv.participants || [];
  const createdBy = conv.createdBy;

  // Step 1: Filter participants
  let filtered = participants.filter(
    (p: ConversationParticipant) => !p.isCreator,
  );

  if (createdBy?.roles === 'admin') {
    filtered = filtered.filter((p) => !p.isCompanyParentBroadcast);
  }

  if (createdBy?.roles === 'agency' && createdBy?.agencyId) {
    // Exclude the creator's own agency from recipients
    filtered = filtered.filter((p) => p.agency?.id !== createdBy.agencyId?.id);
  }

  // Step 2: Map names
  const names = filtered
    .map((p) => {
      if (p.agency) return p.agency.name;
      if (p.user) return formatName(p.user.forename, p.user.surname);
      if (p.isCompanyParentBroadcast) {
        return getUmbrellaCompanyName(currentUser) || 'Admin';
      }
      return 'Unknown';
    })
    .filter(Boolean);

  return names.length > 0 ? names : ['Unknown'];
};

/**
 * Get recipient display names from participants (joined string)
 */
export const getConversationRecipientNames = (
  conv: Conversation,
  currentUser: CurrentUser,
): string => {
  return getConversationRecipientNameList(conv, currentUser).join(', ');
};

/**
 * Get display name with From/To label
 */
export const getConversationDisplayName = (
  conv: Conversation,
  currentUser: CurrentUser,
  isBureau: boolean,
): DisplayResult => {
  const isSentByMe = conv.createdBy?.id === currentUser?.id;

  if (isSentByMe) {
    const names = getConversationRecipientNameList(conv, currentUser);
    return {
      label: 'To',
      name: names.join(', '),
      names,
    };
  }

  const senderName = getConversationSenderName(conv, currentUser, isBureau);
  return {
    label: 'From',
    name: senderName,
    names: [senderName],
  };
};

/**
 * Get sender display name for a chat bubble message
 * Uses participant data from conversation for role-based display
 */
export const getMessageSenderName = (
  senderUserId: string,
  conv: Conversation,
  currentUser: CurrentUser,
  isBureau: boolean,
  messageSender?: { forename: string; surname: string },
): string => {
  // If sender is the conversation creator, use createdBy data (has roles, agencyName)
  if (conv.createdBy?.id === senderUserId) {
    return getConversationSenderName(conv, currentUser, isBureau);
  }

  // Find participant by userId or by user.id (agency participants have userId: null)
  const participants = conv.participants || [];
  const participant =
    participants.find(
      (p: ConversationParticipant) => p.userId === senderUserId,
    ) ||
    participants.find(
      (p: ConversationParticipant) => p.user?.id === senderUserId,
    );

  if (participant) {
    // Bureau: forename + surname
    if (isBureau) {
      return formatName(participant.user?.forename, participant.user?.surname);
    }

    // Umbrella: agency participant → agency name
    if (participant.agency) {
      return participant.agency.name;
    }

    // Umbrella: admin participant → companyName
    if (participant.user?.roles === 'admin') {
      return (
        getUmbrellaCompanyName(currentUser) ||
        formatName(participant.user?.forename, participant.user?.surname)
      );
    }

    // Umbrella: broadcast participant → companyName
    if (participant.isCompanyParentBroadcast) {
      return getUmbrellaCompanyName(currentUser) || 'Admin';
    }

    if (participant.user) {
      return formatName(participant.user.forename, participant.user.surname);
    }
  }

  // No participant match — sender might be from a broadcast or agency participant (userId: null)
  if (!isBureau) {
    // Check agency participants
    const agencyParticipant = participants.find(
      (p) => p.agency && !p.isCreator,
    );
    if (agencyParticipant?.agency) {
      return agencyParticipant.agency.name;
    }

    // Check broadcast participant (admin)
    const hasBroadcast = participants.some((p) => p.isCompanyParentBroadcast);
    if (hasBroadcast) {
      return getUmbrellaCompanyName(currentUser) || 'Admin';
    }
  }

  // Fallback to message.sender data
  if (messageSender) {
    return formatName(messageSender.forename, messageSender.surname);
  }

  return 'Unknown';
};
